import { motion } from "framer-motion";
import type { IconType } from "react-icons";
import { FiDownload, FiDownloadCloud } from "react-icons/fi";
import SafeIcon from "./SafeIcon";
import type { ConvertedItem, ConverterTheme } from "./types";

interface ResultCardProps {
  item: ConvertedItem;
  onDownload: (item: ConvertedItem) => void | Promise<void>;
  index: number;
  icon: IconType;
  formatBytes: (bytes: number) => string;
  theme: ConverterTheme;
}

const ResultCard = ({
  item,
  onDownload,
  index,
  icon,
  formatBytes,
  theme,
}: ResultCardProps) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.04 }}
    className="group flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white/60 p-3 transition-[border-color,box-shadow,background-color] hover:shadow-md dark:border-slate-700 dark:bg-slate-800/60"
  >
    <div className="flex min-w-0 items-center gap-3">
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border ${theme.resultIcon}`}
      >
        <SafeIcon icon={icon} className="text-xl" />
      </div>
      <div className="min-w-0">
        <p
          className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100"
          title={item.name}
        >
          {item.name}
        </p>
        <div className="mt-0.5 flex items-center gap-2">
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${theme.resultBadge}`}
          >
            {item.ext}
          </span>
          <span className="text-[10px] text-slate-400">
            {formatBytes(item.size)}
          </span>
        </div>
      </div>
    </div>

    <button
      type="button"
      onClick={() => onDownload(item)}
      aria-label={`Download ${item.name}`}
      className={`flex shrink-0 items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-bold transition-[background-color,border-color,color,box-shadow] group-hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 ${theme.focusRing} ${theme.resultDownloadButton}`}
    >
      <SafeIcon icon={FiDownload} />
      <span className="hidden sm:inline">Download</span>
    </button>
  </motion.div>
);

interface ResultsPanelProps {
  converted: ConvertedItem[];
  onDownloadSingle: (item: ConvertedItem) => void | Promise<void>;
  onDownloadAll: () => void | Promise<void>;
  icon: IconType;
  formatBytes: (bytes: number) => string;
  mediaLabel: string;
  theme: ConverterTheme;
}

const ResultsPanel = ({
  converted,
  onDownloadSingle,
  onDownloadAll,
  icon,
  formatBytes,
  mediaLabel,
  theme,
}: ResultsPanelProps) => (
  <div className="glass rounded-3xl p-5 shadow-xl md:p-6">
    <div className="mb-5 flex items-center justify-between">
      <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
        Results · {converted.length} file{converted.length > 1 ? "s" : ""}
      </h3>
      {converted.length > 1 && (
        <button
          type="button"
          onClick={onDownloadAll}
          className={`flex items-center gap-2 rounded-xl bg-gradient-to-r px-5 py-2.5 text-sm font-bold text-white shadow-lg transition-[background-color,box-shadow,transform] active:scale-95 focus-visible:outline-none focus-visible:ring-2 ${theme.focusRing} focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950 ${theme.primaryButton} ${theme.primaryShadow}`}
        >
          <SafeIcon icon={FiDownloadCloud} />
          Download All (.zip)
        </button>
      )}
    </div>

    <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
      {converted.map((item, index) => (
        <ResultCard
          key={item.url}
          item={item}
          onDownload={onDownloadSingle}
          index={index}
          icon={icon}
          formatBytes={formatBytes}
          theme={theme}
        />
      ))}
    </div>

    {converted.length === 1 && (
      <p className="mt-4 text-center text-xs text-slate-400">
        Click <strong>Download</strong> above to save your converted{" "}
        {mediaLabel.toLowerCase()}.
      </p>
    )}
  </div>
);

export default ResultsPanel;
